import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  getAlternatePlans, deleteAlternatePlan,
  getStudentScheduleChanges, cancelScheduleChange, getStaff,
} from "../../lib/api";
import StudentSignupsCard from "./StudentSignupsCard";
import { StudentClassScheduleSection } from "./StudentClassScheduleSection";
import StudentScheduleCalendarPrint from "./StudentScheduleCalendarPrint";
import ScheduleChangeForm from "./ScheduleChangeForm";
import AlternatePlanForm from "./AlternatePlanForm";
import { ScheduleChangeHistory, BeforeAfterWeekly } from "./ScheduleChangeHistory";
import { describePlanSchedule, describePlanTimes } from "../../lib/altPlans";

const DEFAULT_SCHEDULE = {
  monday: { expected: true, start: "08:30", end: "14:00" },
  tuesday: { expected: true, start: "08:30", end: "14:00" },
  wednesday: { expected: true, start: "08:30", end: "14:00" },
  thursday: { expected: true, start: "08:30", end: "14:00" },
  friday: { expected: true, start: "08:30", end: "12:00" },
};

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const STANDARD_FULL_START = "08:30";
const STANDARD_FULL_END = "14:00";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function dayTypeBadge(start, end) {
  const s = (start || STANDARD_FULL_START).trim();
  const e = (end || STANDARD_FULL_END).trim();
  if (s >= "12:00" || e <= "12:00") return { label: "Half Day", cls: "bg-amber-100 text-amber-800" };
  const lateStart = s > STANDARD_FULL_START;
  const earlyEnd = e < STANDARD_FULL_END;
  if (lateStart && earlyEnd) return { label: "Partial Day", cls: "bg-orange-100 text-orange-800" };
  if (lateStart) return { label: "Late Start", cls: "bg-orange-100 text-orange-800" };
  if (earlyEnd) return { label: "Early Release", cls: "bg-orange-100 text-orange-800" };
  return { label: "Full Day", cls: "bg-green-100 text-green-800" };
}

export function ScheduleTab({ student, setStudent, studentId, onGoToAttendance }) {
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [changes, setChanges] = useState([]);
  const [staff, setStaff] = useState([]);

  const fetchChanges = useCallback(async () => {
    try {
      const res = await getStudentScheduleChanges(studentId);
      setChanges(res.data || []);
    } catch { /* silent */ }
  }, [studentId]);

  useEffect(() => {
    fetchChanges();
    getStaff().then((res) => setStaff(res.data || [])).catch(() => setStaff([]));
  }, [fetchChanges]);

  const getScheduleForDay = (day) => (student?.schedule || {})[day] || DEFAULT_SCHEDULE[day];

  const upcoming = changes.filter((c) => c.status === "upcoming");
  const lastChange = changes.find(
    (c) => ["weekly_schedule", "initial"].includes(c.change_type) && !["upcoming", "cancelled"].includes(c.status)
  );

  const handleScheduleSaved = (r) => {
    if (!r?.scheduled && r?.appliedSchedule) {
      setStudent((prev) => ({ ...prev, schedule: r.appliedSchedule }));
    }
    setScheduleDialogOpen(false);
    fetchChanges();
  };

  const handleCancelUpcoming = async (changeId) => {
    if (!window.confirm("Cancel this upcoming schedule change?")) return;
    try {
      await cancelScheduleChange(changeId);
      toast.success("Upcoming change cancelled");
      fetchChanges();
    } catch { toast.error("Failed to cancel"); }
  };

  const studentName = (student?.preferred_name || student?.first_name || "") + " " + (student?.last_name || "");

  return (
    <div className="space-y-6">
      <StudentSignupsCard studentId={studentId} />

      {/* Current Schedule */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Current Schedule</CardTitle>
              <CardDescription>Default: M-Th full days, Fridays half days. First Wednesday of month is half day.</CardDescription>
              {lastChange && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="schedule-last-changed">
                  Last changed {fmtDate(lastChange.effective_date)}
                  {lastChange.created_by_name ? ` by ${lastChange.created_by_name}` : ""}
                  {lastChange.reason_category ? `: ${lastChange.reason_category}` : ""}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StudentScheduleCalendarPrint student={student} studentId={studentId} />
              <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                <Button variant="outline" data-testid="edit-schedule-btn" onClick={() => setScheduleDialogOpen(true)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  </svg>
                  Make Schedule Change
                </Button>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Make a Schedule Change</DialogTitle>
                  </DialogHeader>
                  {scheduleDialogOpen && (
                    <ScheduleChangeForm
                      student={student}
                      studentId={studentId}
                      staff={staff}
                      onSaved={handleScheduleSaved}
                      onCancel={() => setScheduleDialogOpen(false)}
                    />
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {WEEKDAYS.map((day) => {
              const schedule = getScheduleForDay(day);
              return (
                <div key={day} className={`text-center p-4 rounded-lg border ${!schedule.expected ? "bg-gray-100 opacity-60" : ""}`}>
                  <h4 className="font-medium mb-2 capitalize">{day}</h4>
                  {schedule.expected ? (
                    <>
                      <p className="text-sm text-muted-foreground">{schedule.start || "08:30"} - {schedule.end || "14:00"}</p>
                      <span className={`text-xs px-2 py-1 rounded mt-2 inline-block ${dayTypeBadge(schedule.start, schedule.end).cls}`}>
                        {dayTypeBadge(schedule.start, schedule.end).label}
                      </span>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Not Expected</p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4">
            <button type="button" className="text-sm text-[#00305E] hover:underline" onClick={() => onGoToAttendance && onGoToAttendance()} data-testid="view-attendance-link">
              View attendance history →
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Changes */}
      {upcoming.length > 0 && (
        <Card data-testid="upcoming-changes-card">
          <CardHeader>
            <CardTitle>Upcoming Changes</CardTitle>
            <CardDescription>Scheduled to take effect automatically on the date shown.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcoming.map((c) => (
              <div key={c.id} className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3" data-testid={`upcoming-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">Effective {fmtDate(c.effective_date)}</p>
                    <p className="text-sm text-gray-700 mt-0.5">{c.summary}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {c.reason_category || "No reason"}{c.approved_by_name ? ` · Approved by ${c.approved_by_name}` : ""}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 h-7 px-2 text-xs shrink-0" onClick={() => handleCancelUpcoming(c.id)} data-testid={`cancel-upcoming-${c.id}`}>Cancel</Button>
                </div>
                {(c.change_type === "weekly_schedule") && (
                  <div className="mt-2">
                    <BeforeAfterWeekly before={c.before} after={c.after} />
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Alternate Plans */}
      <AlternatePlansSection studentId={studentId} staff={staff} onChanged={fetchChanges} />

      {/* Student Class Schedule from Master Schedule */}
      <StudentClassScheduleSection studentId={studentId} />

      {/* Schedule Change History */}
      <ScheduleChangeHistory studentName={studentName.trim()} changes={changes} />
    </div>
  );
}

function AlternatePlansSection({ studentId, staff = [], onChanged }) {
  const [plans, setPlans] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showEnded, setShowEnded] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await getAlternatePlans({ student_id: studentId, active_only: false });
      setPlans(res.data || []);
    } catch { /* silent */ }
  }, [studentId]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const activePlans = plans.filter((p) => p.active);
  const endedPlans = plans.filter((p) => !p.active);

  const openAdd = () => { setEditingPlan(null); setDialogOpen(true); };
  const openEdit = (plan) => { setEditingPlan(plan); setDialogOpen(true); };

  const handleSaved = () => {
    setDialogOpen(false);
    setEditingPlan(null);
    fetchPlans();
    onChanged && onChanged();
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("End this alternate plan? It stays in the schedule history.")) return;
    try { await deleteAlternatePlan(planId); toast.success("Plan ended"); fetchPlans(); onChanged && onChanged(); }
    catch { toast.error("Failed to end plan"); }
  };

  const renderPlan = (plan) => (
    <div key={plan.id} className={`p-4 rounded-lg border ${plan.active ? "bg-amber-50/50 border-amber-200" : "bg-gray-50 border-gray-200 opacity-70"}`} data-testid={`alt-plan-${plan.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-semibold text-sm flex items-center gap-2">
            {plan.destination}
            {plan.full_day_out && <span className="inline-block rounded-full bg-[#00305E] text-white text-[10px] font-medium px-2 py-0.5" data-testid={`alt-plan-fullout-badge-${plan.id}`}>Full day out</span>}
            {!plan.active && <span className="inline-block rounded-full bg-rose-100 text-rose-700 text-[10px] font-medium px-2 py-0.5">Ended</span>}
          </p>
          <p className="text-sm text-muted-foreground mt-0.5">
            <span className="font-medium">{describePlanSchedule(plan)}</span>
            {plan.full_day_out ? " · Out of building all day (excused)" : ` · ${describePlanTimes(plan)}`}
          </p>
          {!plan.full_day_out && (plan.missed_block_labels || []).length > 0 && (
            <p className="text-xs text-amber-700 mt-0.5">Misses {plan.missed_block_labels.join(", ")}</p>
          )}
          {plan.notes && <p className="text-xs text-muted-foreground mt-1">{plan.notes}</p>}
        </div>
        {plan.active && (
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openEdit(plan)} data-testid={`alt-plan-edit-${plan.id}`}>Edit</Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500" onClick={() => handleDelete(plan.id)} data-testid={`alt-plan-delete-${plan.id}`}>End</Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Alternate Plans</CardTitle>
            <CardDescription>Internships, jobs, or off-campus activities during school hours</CardDescription>
          </div>
          <Button size="sm" className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={openAdd} data-testid="add-alternate-plan-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
            Add Plan
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {activePlans.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground">No active alternate plans. Add one for internships or off-campus activities.</p>
        ) : (
          <div className="space-y-3">{activePlans.map(renderPlan)}</div>
        )}
        {endedPlans.length > 0 && (
          <div className="mt-3">
            <button type="button" className="text-sm text-[#00305E] hover:underline" onClick={() => setShowEnded((s) => !s)} data-testid="toggle-ended-plans">
              {showEnded ? "Hide" : "Show"} ended plans ({endedPlans.length})
            </button>
            {showEnded && <div className="space-y-3 mt-3" data-testid="ended-plans-list">{endedPlans.map(renderPlan)}</div>}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPlan ? "Edit" : "Add"} Alternate Plan</DialogTitle></DialogHeader>
          {dialogOpen && (
            <AlternatePlanForm
              studentId={studentId}
              staff={staff}
              editingPlan={editingPlan}
              onSaved={handleSaved}
              onCancel={() => setDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
